import { useState, useEffect, useCallback } from "react";
import { useAppStore } from "../../store/app";
import { readCustomers, saveCustomers } from "../../lib/api/customers";
import { listAllCandidates } from "../../lib/api/profile";
import type { Customer } from "../../lib/customers/types";

// ── Icons ──────────────────────────────────────────────────────────────────

function PlusIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}
function EditIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

interface StaffOption { folder: string; name: string; }

function emptyCustomer(): Omit<Customer, "id" | "created_at" | "updated_at"> {
  return {
    name: "", website: "", contact_name: "", contact_email: "", contact_phone: "",
    account_manager_folder: "", sales_manager_folder: "", relationships_manager_folder: "",
  };
}

const inputCls = "rounded-md border border-slate-200 dark:border-slate-600 bg-white dark:bg-[#131825] px-2.5 py-1.5 text-sm text-slate-800 dark:text-slate-200 focus:border-indigo-400 focus:ring-1 focus:ring-indigo-400 focus:outline-none w-full";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <label className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</label>
      {children}
    </div>
  );
}

// ── Add / Edit Modal ────────────────────────────────────────────────────────

interface ModalProps {
  customer: Customer | null; // null = new
  staff: StaffOption[];
  onSave: (c: Omit<Customer, "id" | "created_at" | "updated_at">) => void;
  onClose: () => void;
}

function CustomerModal({ customer, staff, onSave, onClose }: ModalProps) {
  const [form, setForm] = useState<Omit<Customer, "id" | "created_at" | "updated_at">>(
    customer
      ? {
          name: customer.name, website: customer.website, contact_name: customer.contact_name,
          contact_email: customer.contact_email, contact_phone: customer.contact_phone,
          account_manager_folder: customer.account_manager_folder,
          sales_manager_folder: customer.sales_manager_folder,
          relationships_manager_folder: customer.relationships_manager_folder,
        }
      : emptyCustomer(),
  );

  function set<K extends keyof typeof form>(key: K, val: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white dark:bg-[#182132] rounded-2xl border border-slate-200/70 dark:border-slate-700/50 shadow-2xl w-full max-w-xl mx-4 overflow-hidden">
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700/60">
          <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100">
            {customer ? "Edit customer" : "Add customer"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-lg leading-none cursor-pointer focus:outline-none">✕</button>
        </div>

        {/* Form */}
        <div className="px-6 py-5 space-y-5 max-h-[75vh] overflow-y-auto">
          {/* Basic info */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Company</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field label="Customer name *">
                  <input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Rabobank" />
                </Field>
              </div>
              <Field label="Website">
                <input className={inputCls} value={form.website} onChange={(e) => set("website", e.target.value)} placeholder="https://..." />
              </Field>
            </div>
          </div>

          {/* Contact person */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Contact person</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field label="Name">
                  <input className={inputCls} value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Full name" />
                </Field>
              </div>
              <Field label="Email">
                <input type="email" className={inputCls} value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="name@company.com" />
              </Field>
              <Field label="Phone">
                <input className={inputCls} value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="+31 6 …" />
              </Field>
            </div>
          </div>

          {/* Adapt staff */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Adapt account team</p>
            <div className="grid grid-cols-1 gap-3">
              <Field label="Account manager">
                <select className={inputCls} value={form.account_manager_folder} onChange={(e) => set("account_manager_folder", e.target.value)}>
                  <option value="">— None —</option>
                  {staff.map((s) => <option key={s.folder} value={s.folder}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Sales manager">
                <select className={inputCls} value={form.sales_manager_folder} onChange={(e) => set("sales_manager_folder", e.target.value)}>
                  <option value="">— None —</option>
                  {staff.map((s) => <option key={s.folder} value={s.folder}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Relationships manager">
                <select className={inputCls} value={form.relationships_manager_folder} onChange={(e) => set("relationships_manager_folder", e.target.value)}>
                  <option value="">— None —</option>
                  {staff.map((s) => <option key={s.folder} value={s.folder}>{s.name}</option>)}
                </select>
              </Field>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 dark:border-slate-700/60">
          <button
            onClick={onClose}
            className="rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-transparent px-4 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors cursor-pointer focus:outline-none"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (form.name.trim()) onSave(form); }}
            disabled={!form.name.trim()}
            className="rounded-lg bg-indigo-600 hover:bg-indigo-700 px-5 py-1.5 text-xs font-semibold text-white transition-colors cursor-pointer focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {customer ? "Save changes" : "Add customer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main screen ─────────────────────────────────────────────────────────────

export default function CustomersScreen() {
  const repoPath = useAppStore((s) => s.repoPath);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [staff, setStaff] = useState<StaffOption[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<"add" | Customer | null>(null);

  const load = useCallback(async () => {
    if (!repoPath) return;
    setLoading(true);
    try {
      const [custs, cands] = await Promise.all([
        readCustomers(repoPath),
        listAllCandidates(repoPath),
      ]);
      setCustomers(custs);
      setStaff(cands.map((c) => ({ folder: c.folder, name: c.name })));
    } finally {
      setLoading(false);
    }
  }, [repoPath]);

  useEffect(() => { load(); }, [load]);

  function staffName(folder: string) {
    return staff.find((s) => s.folder === folder)?.name ?? "—";
  }

  async function handleSave(form: Omit<Customer, "id" | "created_at" | "updated_at">) {
    if (!repoPath) return;
    const now = new Date().toISOString();
    let updated: Customer[];
    if (modal && typeof modal === "object") {
      // editing existing
      updated = customers.map((c) =>
        c.id === modal.id ? { ...c, ...form, updated_at: now } : c,
      );
    } else {
      // adding new
      const newCustomer: Customer = {
        id: crypto.randomUUID(),
        ...form,
        created_at: now,
        updated_at: now,
      };
      updated = [...customers, newCustomer];
    }
    await saveCustomers(repoPath, updated);
    setCustomers(updated);
    setModal(null);
  }

  const filtered = customers.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.contact_name.toLowerCase().includes(q) ||
      c.website.toLowerCase().includes(q)
    );
  });

  const sorted = [...filtered].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">Customers</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            {customers.length === 0
              ? "No customers yet"
              : `${customers.length} customer${customers.length !== 1 ? "s" : ""}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customers…"
              className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#182132] px-3.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none w-52"
            />
            {search && (
              <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs cursor-pointer">✕</button>
            )}
          </div>
          <button
            onClick={load}
            className="rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-700 dark:hover:bg-slate-600 border border-slate-200 dark:border-slate-600 px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 transition-colors cursor-pointer focus:outline-none"
          >
            ↻ Refresh
          </button>
          <button
            onClick={() => setModal("add")}
            className="flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 px-3 py-1.5 text-xs font-semibold text-white transition-colors cursor-pointer focus:outline-none"
          >
            <PlusIcon /> Add customer
          </button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <span className="h-7 w-7 animate-spin rounded-full border-2 border-slate-300 border-t-slate-800" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <p className="text-sm text-slate-400">
            {search ? "No customers match your search" : "No customers yet — add your first one"}
          </p>
          {!search && (
            <button
              onClick={() => setModal("add")}
              className="flex items-center gap-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 px-4 py-1.5 text-xs font-semibold text-white transition-colors cursor-pointer focus:outline-none"
            >
              <PlusIcon /> Add customer
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-[#182132] shadow-xs overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100 dark:border-slate-700/60 bg-slate-50/60 dark:bg-slate-800/40">
                <th className="py-3 px-5 text-[10px] font-bold uppercase tracking-wider text-slate-400 w-56">Customer</th>
                <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Contact</th>
                <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Account manager</th>
                <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Sales manager</th>
                <th className="py-3 px-4 text-[10px] font-bold uppercase tracking-wider text-slate-400">Relationships</th>
                <th className="py-3 px-4 w-16" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50 dark:divide-slate-700/40">
              {sorted.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors group">
                  <td className="py-3.5 px-5">
                    <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">{c.name}</p>
                    {c.website && (
                      <a
                        href={c.website}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-indigo-500 hover:underline truncate max-w-[200px] block"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {c.website.replace(/^https?:\/\//, "")}
                      </a>
                    )}
                  </td>
                  <td className="py-3.5 px-4">
                    {c.contact_name ? (
                      <>
                        <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{c.contact_name}</p>
                        {c.contact_email && <p className="text-xs text-slate-400 truncate max-w-[180px]">{c.contact_email}</p>}
                      </>
                    ) : (
                      <span className="text-xs text-slate-300 dark:text-slate-600 italic">—</span>
                    )}
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="text-xs text-slate-600 dark:text-slate-300">{staffName(c.account_manager_folder)}</span>
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="text-xs text-slate-600 dark:text-slate-300">{staffName(c.sales_manager_folder)}</span>
                  </td>
                  <td className="py-3.5 px-4">
                    <span className="text-xs text-slate-600 dark:text-slate-300">{staffName(c.relationships_manager_folder)}</span>
                  </td>
                  <td className="py-3.5 px-4 text-right">
                    <button
                      onClick={() => setModal(c)}
                      className="p-1.5 rounded-md text-slate-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors cursor-pointer focus:outline-none opacity-0 group-hover:opacity-100"
                      title="Edit"
                    >
                      <EditIcon />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {modal !== null && (
        <CustomerModal
          customer={typeof modal === "object" ? modal : null}
          staff={staff}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
}
